import React from "react";
import ArticleTemplate from "../../../components/ArticleTemplate";

const SubtleSolidityBugs = () => {
  return (
    <ArticleTemplate
      title="The 3 Most Subtle Solidity Bugs We Found in Audits (And How We Found Them)"
      subtitle="This is the first article in our three-part series on protocol security."
      author="Laurence Kirk"
      date="November 19th, 2025"
      readingTime="11 min read"
      tags={["Article", "Audit", "Security", "EVM", "Solidity", "Case Study"]}
      canonicalUrl="https://security.extropy.io/articles/most-subtle-solidity-bugs-from-audits"
    >
      <h2>Introduction: Why Manual Review Will Always Be Non-Negotiable</h2>
      <p>
        In smart contract auditing, automated tools like Slither or Aderyn are an
        essential first line of defence. They are excellent at finding known
        anti-patterns such as re-entrancy, incorrect visibility, or unsafe
        operations.
      </p>
      <p>
        However, the most catastrophic vulnerabilities—the ones that automated
        tools cannot find—are almost always flaws in the protocol&apos;s unique
        business logic. These bugs arise not from a single bad line of code, but
        from a &quot;correct&quot; implementation of a flawed assumption.
      </p>
      <p>
        Finding them requires an expert, adversarial, and creative manual review
        process. You must understand what the code intends to do, and then find a
        way to break that intention.
      </p>
      <p>
        This article shares three real, subtle, and high-impact vulnerabilities
        our team discovered during recent EVM audits. They are case studies in why
        expert, manual review is the most critical part of any serious audit.
      </p>

      <hr />

      <h2>Bug #1: The &quot;Poisoned&quot; Invariant - An Accounting System Broken by 1 Gwei</h2>
      <p>
        <strong>Protocol Type:</strong> Liquid Staking Protocol <br />
        <strong>Vulnerability:</strong> [HIGH] Code Invariant Can Be Broken
      </p>
      <h3>The Context</h3>
      <p>
        We audited a liquid staking protocol where users deposited <code>TKN</code> into a{" "}
        <code>Vault</code> contract and received the liquid-staked token <code>sTKN</code> in
        return. The entire accounting model relied on a fundamental invariant:
      </p>
      <blockquote>
        The total <code>sTKN</code> tokens minted (the <code>totalSupply</code>) MUST always
        equal the total <code>shares</code> held by the <code>sTKN</code> token contract inside
        the <code>Vault</code>.
      </blockquote>
      <p>
        This invariant fed the valuation logic to ensure every user could redeem
        tokens proportionally.
      </p>
      <h3>The Bug: A Subtle Logic Flaw</h3>
      <p>
        We discovered the invariant could be permanently broken through a little-known
        EVM property: force-sending Ether via <code>selfdestruct</code>. An attacker could
        deploy a contract, fund it with 1 gwei, and call <code>selfdestruct</code> targeting
        the <code>Vault</code>.
      </p>
      <p>
        Because the transfer bypasses <code>receive()</code> or <code>deposit()</code> flows,
        the <code>Vault</code> balance increases with no corresponding update to the share
        ledger.
      </p>
      <h3>The Chain Reaction</h3>
      <p>
        The dust sat idle until the oracle invoked <code>rebalance()</code>. The function
        read the inflated balance, mistook it for rewards, and poisoned the internal
        accounting. The next legitimate deposit relied on a tainted pool value and
        issued the wrong number of shares. The &quot;golden&quot; invariant was now broken
        forever.
      </p>
      <h3>Why Automated Tools Fail (And Why Experts Succeed)</h3>
      <p>
        Static analysis sees correct math and will never flag a business assumption
        such as &quot;balance increases only via deposits&quot;. Manual review allowed us to:
      </p>
      <ol>
        <li>Identify the implicit invariant.</li>
        <li>Theorise how to break it.</li>
        <li>Spot <code>selfdestruct</code> as a viable poison vector.</li>
        <li>Write a PoC test proving the invariant break.</li>
      </ol>
      <p>
        <strong>The Fix:</strong> The client now relies on the <code>Vault</code>&apos;s internal{" "}
        <code>totalShares()</code> which cannot be manipulated by force-sent funds.
      </p>

      <hr />

      <h2>Bug #2: The &quot;Perpetual Interest-Free Loan&quot; - A Business Logic Flaw</h2>
      <p>
        <strong>Protocol Type:</strong> P2P Lending Protocol <br />
        <strong>Vulnerability:</strong> [LOW] Borrower can avoid paying loan interest if{" "}
        <code>loan.fixedInterestAmount</code> is zero
      </p>
      <h3>The Context</h3>
      <p>
        Borrowers could take loans with a <code>fixedInterestAmount</code> and a variable{" "}
        <code>accruingInterestAPR</code>. The <code>AccrueInterest</code> function used{" "}
        <code>Math.mulDiv</code> with a large denominator (5.256e12) to convert APR to a
        per-minute rate.
      </p>
      <h3>The Bug: An Integer Division Exploit</h3>
      <p>
        By setting <code>fixedInterestAmount = 0</code>, a borrower ensured that for roughly
        the first 50 minutes the numerator stayed smaller than the denominator, so
        integer division rounded down to zero. Interest due became <code>0 + 0 = 0</code>.
      </p>
      <h3>The Attack Vector</h3>
      <p>
        A bot could take a large loan with zero fixed interest, wait 49 minutes, call{" "}
        <code>refLoan()</code>, pay no interest, and repeat indefinitely. The protocol&apos;s
        lenders effectively funded a perpetual, interest-free loan.
      </p>
      <h3>Why Automated Tools Fail (And Why Experts Succeed)</h3>
      <p>
        Automated tools confirmed the math was &quot;safe&quot; yet missed the economic disaster.
        Manual auditors assessed the financial intent, measured attacker cost versus
        damage, and recognised the protocol&apos;s business model collapsing if borrowers
        could legally pay zero.
      </p>

      <hr />

      <h2>Bug #3: The &quot;Gas-Limit DoS&quot; - Using a <code>view</code> Function as a Weapon</h2>
      <p>
        <strong>Protocol Type:</strong> DAO Voting / Staking Protocol <br />
        <strong>Vulnerability:</strong> [MEDIUM] Staker Power Calculation Denial Of Service
      </p>
      <h3>The Context</h3>
      <p>
        The DAO relied on a <code>view</code> function, <code>stakerPowerAt()</code>, to compute
        voting power by iterating over every stake an address owned. The design assumed
        users held a reasonable number of stakes.
      </p>
      <h3>The Bug: A Flaw in Economic Assumptions</h3>
      <p>
        A malicious user could create thousands of tiny stakes. Each call to{" "}
        <code>stakerPowerAt()</code> now looped thousands of times and quickly exceeded
        the RPC node gas limit for <code>eth_call</code>, breaking the voting UI and any
        automation that relied on the view.
      </p>
      <h3>Why Automated Tools Fail (And Why Experts Succeed)</h3>
      <p>
        Static analysis might flag the loop as &quot;expensive&quot; but never as a deliberate
        DoS vector. Manual auditors evaluated the attacker&apos;s cost (gas to create stakes)
        versus protocol damage and identified the availability threat hidden in a
        supposedly &quot;free&quot; function.
      </p>

      <hr />

      <h2>Conclusion: You Don&apos;t Find What You Don&apos;t Look For</h2>
      <p>
        Automated tools are a commodity. They check for what&apos;s known. Our audit process
        is designed to find what is novel. We uncover subtle flaws in unique business
        logic, financial loopholes in economic models, and adversarial assumptions
        that haven&apos;t been stress-tested.
      </p>
      <p>
        This combination of deep manual review, a clear understanding of incentives,
        and proprietary tooling forms the core of our audit process. We find what
        standard tools miss.
      </p>
      <p>
        In our next article,{" "}
        <a href="./beyond-the-code">&quot;Beyond the Code&quot;</a>, we explore the
        expert, human-led methodology that allows our team to uncover these
        vulnerabilities.
      </p>

    </ArticleTemplate>
  );
};

export default SubtleSolidityBugs;
